import { apiFetch } from './api'
import type { CallMediaStats } from '../composables/useDialer'

// ---------------------------------------------------------------------------
// Types — mirror Pydantic response models in api/app/api/routes/dialer.py
// ---------------------------------------------------------------------------

export interface CallerQueueTopSignal {
  kind: string
  label: string // friendly, action-style chip label (e.g. "SDR reached voicemail")
  polarity: string
  summary: string | null
  occurred_at: string
  intensity: number
}

export interface CallerQueueLastConnect {
  outcome: string | null
  at: string | null
  from_e164: string | null
}

// The voicemail tied to the last connect (present only when the last dial on
// the number was a voicemail). `text` is the agent script (null for a human-
// left VM); `from_e164` is the number it was left from; `at` is when.
export interface CallerQueueVoicemail {
  text: string | null
  from_e164: string | null
  at: string | null
}

// Raw CRM/social identifiers — build deep links FE-side (reuse the existing
// Salesforce/LinkedIn helpers + the tenant's SF base_url / HubSpot portal id).
export interface CallerQueueCrmIds {
  salesforce_contact_id: string | null
  salesforce_lead_id: string | null
  hubspot_contact_id: string | null
  pipedrive_person_id: string | null
  outreach_prospect_id: number | null
  linkedin_url: string | null
}

/** Whether a line (or a person) can be dialled right now.
 *  'ready'                   — callable
 *  'cooldown'                — dialled minutes ago; a rep may still override
 *  'blocked_no_local_number' — no pool number for that country; never callable
 */
export type Dialability = 'ready' | 'cooldown' | 'blocked_no_local_number'

/** One of the contact's numbers, and the queue entry that backs it. */
export interface QueuedNumber {
  entry_id: string
  e164: string
  phone_type: string | null
  dialability: Dialability
  cooldown_until: string | null
  country: string | null
  timezone: string
  last_connect: CallerQueueLastConnect | null
}

export interface CallerQueueEntry {
  // One row per person. `id` / `prospect_e164` describe the number we would dial
  // first; `numbers` carries the rest, best-first.
  id: string
  contact_id: string
  prospect_e164: string
  numbers: QueuedNumber[]
  contact_name: string | null
  contact_title: string | null
  account_id: string | null
  account_name: string | null
  account_domain: string | null
  timezone: string
  city: string
  country: string | null // ISO region for the prospect's number (e.g. "GB")
  // null for an ad-hoc call list entry — ad-hoc lists have no play behind them.
  sales_play_id: string | null
  sales_play_name: string | null
  sales_play_description: string | null
  membership_id: string | null
  label: 'hot' | 'warm' | 'cold'
  score: number
  human_attempts: number
  last_human_attempt_at: string | null
  why_now: string | null
  // Per-PERSON: the best of `numbers`.
  dialability: Dialability
  // When the soonest line wakes up. Only set when every line is resting.
  cooldown_until: string | null
  top_signals: CallerQueueTopSignal[]
  last_connect: CallerQueueLastConnect | null
  voicemail: CallerQueueVoicemail | null
  reachout_id: string | null
  notes: string | null
  crm_ids: CallerQueueCrmIds
}

export interface ClaimResponse {
  entry_id: string
  dispatch_id: string
  prospect_e164: string
  reachout_id: string | null
  // The pool number the call will dial from. Pre-claimed by the backend so
  // the /voice webhook never has to. Used by the UI to display the from-
  // number to the SDR if desired.
  from_phone_e164: string
  from_phone_id: string
}

export interface AccessTokenResponse {
  access_token: string
  identity: string
  expires_at: string
}

export interface AgentActivityRow {
  // Null when the entry has been queued but the scheduler hasn't yet
  // materialised a dispatch (fresh approval or capacity-parked).
  dispatch_id: string | null
  entry_id: string
  prospect_e164: string
  status: string
  outcome: string | null
  attempt_no: number
  scheduled_for: string | null
  started_at: string | null
  finished_at: string | null
  error_class: string | null
  // >0 means the scheduler has tried to reserve a pool slot at least once
  // and parked (capacity exhausted). Combined with dispatch_id === null,
  // signals "stuck — operator should add pool numbers / widen window".
  unscheduled_attempts: number
  // Set when the scheduler has terminally exit-stamped the entry. The
  // backend keeps recently-exited rows in this list for 24h so operators
  // see "what just happened"; the UI uses these to render a terminal pill
  // / suppress the awaiting-dispatch hint instead of treating the entry
  // as still-active.
  exit_at: string | null
  exit_status: string | null

  contact_id: string | null
  contact_name: string | null
  contact_title: string | null
  contact_timezone: string | null
  contact_city: string | null
  account_id: string | null
  account_name: string | null
  sequence_id: string | null
  sequence_name: string | null
  sequence_step_label: string | null
}

export interface EndCallPayload {
  dispatch_id: string
  started_at?: string
  ended_at?: string
  duration_seconds?: number
  twilio_call_sid?: string
  disposition_code?: string
  disposition_label?: string
  notes?: string
  exit_status?: string
  client_media_stats?: CallMediaStats
}

export interface EndCallResponse {
  entry_id: string
  exit_status: string | null
  enrollment_advanced: boolean
  // false = the dispatch was already terminal.
  logged?: boolean
}

// ---------------------------------------------------------------------------
// REST clients
// ---------------------------------------------------------------------------

export type QueueSortMode =
  | 'sales_play'
  | 'least_attempts'
  | 'longest_since'
  | 'name_asc'
  | 'name_desc'

export interface DialerQueueParams {
  salesPlay?: string | null
  accountId?: string | null
  sort?: QueueSortMode
}

export function getDialerQueue(params: DialerQueueParams = {}): Promise<CallerQueueEntry[]> {
  const qs = new URLSearchParams()
  if (params.salesPlay) qs.set('sales_play_id', params.salesPlay)
  if (params.accountId) qs.set('account_id', params.accountId)
  if (params.sort && params.sort !== 'sales_play') qs.set('sort', params.sort)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return apiFetch<CallerQueueEntry[]>(`/api/dialer/queue${suffix}`)
}

// ---------------------------------------------------------------------------
// The rep's dial lists — dial-only sales plays, presented as lists. People arrive
// from the LeadIQ extension and leave by removal. Distinct from the dialing
// "session" (useDialerSession): that is one dialing run.
// ---------------------------------------------------------------------------

export interface DialListRemoveResponse {
  removed: number // memberships exited
}

/** Off every one of the caller's lists; a later re-push from the extension re-adds them. */
export function removeFromDialList(contactIds: string[]): Promise<DialListRemoveResponse> {
  return apiFetch<DialListRemoveResponse>('/api/dialer/list/remove', {
    method: 'POST',
    body: { contact_ids: contactIds },
    responseType: 'json',
  })
}

// ---------------------------------------------------------------------------
// First-time onboarding: the rep's captured LeadIQ leads, offered as the first
// people to call. Read and added server-side through Raven's leadiq-play link.
// ---------------------------------------------------------------------------

export interface CapturedLead {
  lead_id: string
  name: string | null
  first_name: string | null
  title: string | null
  company: string | null
  phone_e164: string
  captured_at: string | null
  contact_id: string | null
}

export function getCapturedLeads(): Promise<CapturedLead[]> {
  return apiFetch<{ leads: CapturedLead[] }>('/api/dialer/onboarding/captured-leads').then((r) => r.leads)
}

export interface AddCapturedLeadResult {
  lead_id: string
  outcome: string
  contact_id: string | null
  reason_code: string | null
}

export function addCapturedLeads(leadIds: string[]): Promise<AddCapturedLeadResult[]> {
  return apiFetch<{ results: AddCapturedLeadResult[] }>('/api/dialer/onboarding/captured-leads/add', {
    method: 'POST',
    body: { lead_ids: leadIds },
    responseType: 'json',
  }).then((r) => r.results)
}

export interface DialerQueueFilterOptions {
  salesPlays: FilterOption[]
  accounts: FilterOption[]
}

/** Plays + accounts present in the live queue population (not the capped
 *  response) — every option is guaranteed to return at least one row. */
export function getDialerQueueFilterOptions(
  params: { salesPlay?: string | null; q?: string | null } = {},
): Promise<DialerQueueFilterOptions> {
  const qs = new URLSearchParams()
  if (params.salesPlay) qs.set('sales_play_id', params.salesPlay)
  if (params.q) qs.set('q', params.q)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return apiFetch<{
    sales_plays: Array<{ id: string; name: string | null }>
    accounts: Array<{ id: string; name: string | null; domain: string | null }>
  }>(`/api/dialer/queue/filter-options${suffix}`).then((r) => ({
    salesPlays: (r.sales_plays || []).map((p) => ({ id: p.id, name: p.name || '(untitled play)' })),
    accounts: (r.accounts || []).map((a) => ({
      id: a.id,
      name: a.name || a.domain || '(unnamed account)',
    })),
  }))
}

export interface DialerEntryNotesResponse {
  entry_id: string
  notes: string | null
}

export function saveDialerEntryNotes(
  entryId: string,
  notes: string | null,
): Promise<DialerEntryNotesResponse> {
  return apiFetch<DialerEntryNotesResponse>(`/api/dialer/entries/${entryId}/notes`, {
    method: 'PUT',
    body: { notes },
    responseType: 'json',
  })
}

export function claimEntry(entryId: string): Promise<ClaimResponse> {
  return apiFetch<ClaimResponse>(`/api/dialer/entries/${entryId}/claim`, {
    method: 'POST',
    responseType: 'json',
  })
}

// Close-outs ride keepalive so one sent from pagehide still lands.
export function endCall(entryId: string, payload: EndCallPayload): Promise<EndCallResponse> {
  return apiFetch<EndCallResponse>(`/api/dialer/entries/${entryId}/end`, {
    method: 'POST',
    body: payload,
    responseType: 'json',
    keepalive: true,
  })
}

export interface ReleasePayload {
  dispatch_id: string
  error_detail?: string
  client_media_stats?: CallMediaStats
}

export interface ReleaseResponse {
  entry_id: string
  dispatch_id: string
  released: boolean
  dispatch_status: string
}

// Abort a claimed-but-not-completed call: frees the pool number immediately and
// leaves the contact re-dialable. Call when a number was claimed but the call
// then failed to place / the SDR aborts before completing.
export function releaseEntry(entryId: string, payload: ReleasePayload): Promise<ReleaseResponse> {
  return apiFetch<ReleaseResponse>(`/api/dialer/entries/${entryId}/release`, {
    method: 'POST',
    body: payload,
    responseType: 'json',
    keepalive: true,
  })
}

export type LineVerdict =
  | 'answered'
  | 'busy'
  | 'declined'
  | 'unavailable'
  | 'rang_out'
  | 'unreachable'
  | 'not_enabled'
  | 'failed'

export interface DispatchStatusResponse {
  dispatch_id: string
  status: string
  started_at: string | null
  finished_at: string | null
  twilio_call_sid: string | null
  outcome: string | null
  error_class: string | null
  error_detail: string | null
  line_verdict?: LineVerdict | null
}

// Read-only dispatch health for the dialing-session bridge-verify: confirms
// /voice ran (status='in_progress') and surfaces a backend-marked failure
// (aborted/failed_* or error_class) so the FE can route a failed call to the
// error screen even when the Twilio SDK reported a "connect".
export function getDispatchStatus(
  entryId: string,
  dispatchId: string,
  signal?: AbortSignal,
): Promise<DispatchStatusResponse> {
  return apiFetch<DispatchStatusResponse>(
    `/api/dialer/entries/${entryId}/dispatch-status?dispatch_id=${encodeURIComponent(dispatchId)}`,
    { responseType: 'json', signal },
  )
}

export function getAgentActivity(opts?: { sequenceId?: string }): Promise<AgentActivityRow[]> {
  const qs = opts?.sequenceId ? `?sequence_id=${encodeURIComponent(opts.sequenceId)}` : ''
  return apiFetch<AgentActivityRow[]>(`/api/dialer/agent-list${qs}`)
}

export function fetchAccessToken(): Promise<AccessTokenResponse> {
  return apiFetch<AccessTokenResponse>('/api/twilio/access-token', {
    method: 'POST',
    responseType: 'json',
  })
}

// ---------------------------------------------------------------------------
// VoicemailSettings (used to drive the dev-override badge in the dialer UI)
// ---------------------------------------------------------------------------

export interface VoicemailSettingsSnapshot {
  enabled: boolean
  mode: string
  voice_identities: Array<Record<string, unknown>>
  development_to_override: string | null
}

export function getVoicemailSettings(): Promise<VoicemailSettingsSnapshot> {
  return apiFetch<VoicemailSettingsSnapshot>('/api/settings/voicemail')
}

// ---------------------------------------------------------------------------
// Pool management
// ---------------------------------------------------------------------------

export interface PoolNumberRow {
  id: string
  e164: string
  country_code: string
  timezone: string
  status: 'warming_up' | 'active' | 'cooling_off' | 'flagged' | 'retired'
  daily_dial_cap: number
  daily_dial_count: number
  last_call_ended_at: string | null
}

export function listPoolNumbers(): Promise<PoolNumberRow[]> {
  return apiFetch<PoolNumberRow[]>('/api/dialer/pool/numbers')
}

export function setCallbackFallback(phoneNumber: string | null): Promise<{
  ok: boolean
  callback_fallback_phone_number: string | null
}> {
  return apiFetch('/api/dialer/settings/callback-fallback', {
    method: 'POST',
    body: { phone_number: phoneNumber },
    responseType: 'json',
  })
}

// Verified callback numbers the tenant can pick from (dev-override target,
// team-default inbound). Grows as reps verify their callback numbers.
export interface VerifiedCallerId {
  phone_number: string
  friendly_name: string
}

export function getTelephonyCallerIds(): Promise<{ caller_ids: VerifiedCallerId[] }> {
  return apiFetch('/api/dialer/telephony/caller-ids')
}

// ---------------------------------------------------------------------------
// Dialer history (read side) — GET /api/dialer/contacts
// One row per PERSON dialed; their lines ride the row's numbers[]. Touches are
// NOT inlined: fetch them per row with getDialerContactTimeline() (or the
// entry-keyed variant for an unlinked number) when the row is expanded.
// ---------------------------------------------------------------------------

export interface DialerHistoryRecording {
  id: string
  status: string
  duration_seconds: number | null
  channels: number | null
  transcript_status: string
  summary: string | null
}

export interface DialerHistoryTimelineEntry {
  attempt_no: number
  prospect_e164: string | null // the line this touch rang
  outcome: string | null // coarse outcome; the last entry carries the rich disposition
  error_class: string | null // set instead of an outcome when the dial itself failed
  at: string | null
  duration_seconds: number | null
  recording: DialerHistoryRecording | null
  call_log_id: string | null
  hubspot_engagement_id: string | null // set once exported to HubSpot
  salesforce_task_id: string | null // set once exported to Salesforce
}

export interface DialerHistoryNote {
  text: string
  author_name: string | null
  at: string | null
}

/** One dialed line of a history person. */
export interface DialerHistoryNumber {
  prospect_e164: string
  entry_id: string | null
  attempts: number
  last_attempt_at: string
  last_disposition_code: string | null
  has_connected: boolean
  agent_only: boolean
  status: string
  note: DialerHistoryNote | null
}

export interface DialerHistoryItem {
  contact_id: string | null
  prospect_e164: string // the primary (most recently attempted) line
  name: string | null
  title: string | null
  account_id: string | null
  account_name: string | null
  account_domain: string | null
  sales_play_name: string | null
  crm_ids: CallerQueueCrmIds
  status: string
  attempts: number // lifetime, summed across every line
  has_connected: boolean
  agent_only: boolean // every attempt was an agent voicemail drop; no human ever dialed
  last_disposition_code: string | null
  last_attempt_at: string | null
  last_attempt_by_user_id: string | null
  last_attempt_by_name: string | null // null when no call_log was written, or the user is gone
  entry_id: string | null // the primary line's entry (timeline fetch for unlinked rows)
  note: DialerHistoryNote | null
  numbers: DialerHistoryNumber[] // most recent first
}

/** Touches for one dialed number (unlinked rows), loaded when the row is expanded. */
export function getDialerEntryTimeline(entryId: string): Promise<DialerHistoryTimelineEntry[]> {
  return apiFetch<DialerHistoryTimelineEntry[]>(`/api/dialer/entries/${entryId}/timeline`)
}

/** Touches across ALL of a contact's dialed lines, numbered globally. */
export function getDialerContactTimeline(
  contactId: string,
): Promise<DialerHistoryTimelineEntry[]> {
  return apiFetch<DialerHistoryTimelineEntry[]>(`/api/dialer/contacts/${contactId}/timeline`)
}

export interface DialerHistoryResponse {
  items: DialerHistoryItem[]
  total: number | null // people; comes with the first page only (null on cursor pages)
  next_cursor: string | null // opaque keyset token; null on the last page
}

export type DialerHistorySort = 'contact' | 'attempts' | 'last_attempt'

export interface DialerHistoryParams {
  salesPlay?: string | null
  accountId?: string | null
  repId?: string | null
  connectedOnly?: boolean
  sort?: DialerHistorySort
  direction?: 'asc' | 'desc'
  limit?: number
  cursor?: string | null
}

export function getDialerHistory(params: DialerHistoryParams = {}): Promise<DialerHistoryResponse> {
  const qs = new URLSearchParams()
  qs.set('limit', String(params.limit ?? 50))
  if (params.cursor) qs.set('cursor', params.cursor)
  if (params.salesPlay) qs.set('sales_play', params.salesPlay)
  if (params.accountId) qs.set('account_id', params.accountId)
  if (params.repId) qs.set('rep_id', params.repId)
  if (params.connectedOnly) qs.set('connected_only', 'true')
  if (params.sort) qs.set('sort', params.sort)
  if (params.direction) qs.set('direction', params.direction)
  return apiFetch<DialerHistoryResponse>(`/api/dialer/contacts?${qs.toString()}`)
}

// Full transcript for one recording (lazy-loaded when the Transcript tab opens).
export interface DialerTranscriptUtterance {
  channel: number
  role: string // 'rep' | 'prospect'
  start: number
  end: number
  transcript: string
}

export interface DialerTranscriptResponse {
  recording_id: string
  transcript_status: string
  transcript_text: string | null
  utterances: DialerTranscriptUtterance[]
  summary: string | null
}

export function getRecordingTranscript(recordingId: string): Promise<DialerTranscriptResponse> {
  return apiFetch<DialerTranscriptResponse>(`/api/dialer/recordings/${recordingId}/transcript`)
}

// ---------------------------------------------------------------------------
// Filter option sources for the history page dropdowns
// ---------------------------------------------------------------------------

export interface FilterOption {
  id: string
  name: string
}

export interface DialerHistoryFilterOptions {
  accounts: FilterOption[]
  salesPlays: FilterOption[]
  reps: FilterOption[]
}

export interface DialerHistoryFilterOptionsParams {
  // Narrows the ACCOUNTS to that play's members; the play and rep lists are never narrowed.
  salesPlay?: string | null
  // Narrows the ACCOUNTS to people that rep last dialed, as the page's Rep filter does.
  repId?: string | null
  // Server-side account name/domain search.
  q?: string | null
}

/** Accounts, plays + reps that actually appear in the history list — every option is
 *  guaranteed to return at least one row. */
export function getDialerHistoryFilterOptions(
  params: DialerHistoryFilterOptionsParams = {},
): Promise<DialerHistoryFilterOptions> {
  const qs = new URLSearchParams()
  if (params.salesPlay) qs.set('sales_play', params.salesPlay)
  if (params.repId) qs.set('rep_id', params.repId)
  if (params.q) qs.set('q', params.q)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return apiFetch<{
    accounts: Array<{ id: string; name: string | null; domain: string | null }>
    sales_plays: Array<{ id: string; name: string | null }>
    reps: Array<{ id: string; name: string | null }>
  }>(`/api/dialer/contacts/filter-options${suffix}`).then((r) => ({
    accounts: (r.accounts || []).map((a) => ({
      id: a.id,
      name: a.name || a.domain || '(unnamed account)',
    })),
    salesPlays: (r.sales_plays || []).map((p) => ({ id: p.id, name: p.name || '(untitled play)' })),
    reps: (r.reps || []).map((u) => ({ id: u.id, name: u.name || '(unknown rep)' })),
  }))
}

// ===========================================================================
// Telephony onboarding (Human Dialer Onboarding)
//
// The projection is the single object the drawer reads. Every GET here is
// member-readable; admin-only writes are hidden client-side via useCurrentUser().isAdmin.
// ===========================================================================

export type ProvisioningStatus = 'pending' | 'provisioning' | 'provisioned' | 'failed'
export type TelephonyReviewState =
  | 'not_started'
  | 'draft'
  | 'pending_review'
  | 'in_review'
  | 'approved'
  | 'rejected'
export type TelephonyCallbackStatus = 'pending' | 'verified' | 'failed'
export type TelephonyPill = 'setup_incomplete' | 'verification_in_progress' | 'verified'
export type PoolStatusLabel = 'Healthy' | 'Attention'
export type DraftKind = 'business_profile' | 'voice_integrity'
export type CallbackTarget = 'own' | 'team'

export interface TelephonyIssue {
  field_key: string | null
  message: string
}

export interface TelephonyProvisioning {
  status: ProvisioningStatus
  numbers_ready: boolean
}

export interface TelephonyBusinessProfile {
  state: TelephonyReviewState
  submitted: boolean
  issues: TelephonyIssue[]
  draft_updated_at: string | null
}

export interface TelephonyVoiceIntegrity {
  state: TelephonyReviewState
  enabled: boolean
  locked: boolean
  draft_updated_at: string | null
}

export interface TelephonyPoolNumber {
  id: string
  e164: string
  status_label: PoolStatusLabel
}

export interface TelephonyPool {
  count: number
  numbers: TelephonyPoolNumber[]
}

export interface TelephonyCallbackDefault {
  phone_number: string | null
  verified: boolean
  pending_number: string | null
}

export interface TelephonyCallingHours {
  days: string[]
  start: string
  end: string
}

export interface TelephonyRecording {
  enabled: boolean
  excluded_regions: string[]
  // Whole-country consent choices (ISO codes) outside US & Canada.
  countries?: string[]
}

export interface TelephonyGuardrails {
  calling_hours: TelephonyCallingHours | null
  recording: TelephonyRecording
  confirmed: boolean
}


export interface TelephonyMeCallback {
  phone_number: string
  label: string
  status: TelephonyCallbackStatus
}

export interface TelephonyMeVoice {
  voice_id: string
  name: string
}

export interface TelephonyMe {
  callback_number: TelephonyMeCallback | null
  // Whether this user allows agent voicemails on their behalf.
  voicemail_enabled: boolean
  agent_voice: TelephonyMeVoice | null
  // Non-null => this user is in development mode (their calls route here).
  dev_override_number: string | null
  can_manage: boolean
}

export interface TelephonyProjection {
  provisioning: TelephonyProvisioning
  business_profile: TelephonyBusinessProfile
  voice_integrity: TelephonyVoiceIntegrity
  call_signing_enabled: boolean
  pool: TelephonyPool
  callback_default: TelephonyCallbackDefault
  guardrails: TelephonyGuardrails
  me: TelephonyMe
  pill: TelephonyPill
  // Server-evaluated LaunchDarkly gate for answering-machine detection. The same
  // value gates the TwiML, so a false here means no verdict will ever arrive.
  amd_enabled: boolean
}

// POST /ensure — idempotent provisioning trigger; returns the projection.
export function ensureTelephony(): Promise<TelephonyProjection> {
  return apiFetch<TelephonyProjection>('/api/dialer/telephony/ensure', {
    method: 'POST',
    responseType: 'json',
  })
}

export function getTelephony(): Promise<TelephonyProjection> {
  return apiFetch<TelephonyProjection>('/api/dialer/telephony')
}

export interface TelephonyDraft {
  data: Record<string, unknown>
  updated_at: string | null
}

export function getTelephonyDraft(kind: DraftKind): Promise<TelephonyDraft> {
  return apiFetch<TelephonyDraft>(`/api/dialer/telephony/drafts/${kind}`)
}

export function putTelephonyDraft(
  kind: DraftKind,
  data: Record<string, unknown>,
): Promise<TelephonyDraft> {
  return apiFetch<TelephonyDraft>(`/api/dialer/telephony/drafts/${kind}`, {
    method: 'PUT',
    body: { data },
    responseType: 'json',
  })
}

// Submit routes return projection facets; callers refresh() after to be safe.
export function submitBusinessProfile(): Promise<TelephonyProjection> {
  return apiFetch<TelephonyProjection>('/api/dialer/telephony/business-profile/submit', {
    method: 'POST',
    responseType: 'json',
  })
}

export function submitVoiceIntegrity(): Promise<TelephonyProjection> {
  return apiFetch<TelephonyProjection>('/api/dialer/telephony/voice-integrity/submit', {
    method: 'POST',
    responseType: 'json',
  })
}

export interface AvailableNumber {
  phone_number: string
  friendly_name: string
  locality: string
  region: string
}

export interface NearbyAreaCodeSuggestion {
  area_code: string
  region: string
  numbers: AvailableNumber[]
}

export interface AvailableNumbersResult {
  numbers: AvailableNumber[]
  suggestions: NearbyAreaCodeSuggestion[]
}

export function getAvailableNumbers(opts?: {
  areaCode?: string
  country?: string
}): Promise<AvailableNumbersResult> {
  const qs = new URLSearchParams()
  if (opts?.areaCode) qs.set('area_code', opts.areaCode)
  qs.set('country', opts?.country || 'US')
  return apiFetch<AvailableNumbersResult>(`/api/dialer/telephony/available-numbers?${qs.toString()}`)
}

export interface AddNumbersResult {
  added: number
  results: Array<{ phone_number: string; ok: boolean; error?: string }>
}

export function addTelephonyNumbers(e164s: string[]): Promise<AddNumbersResult> {
  return apiFetch<AddNumbersResult>('/api/dialer/telephony/numbers', {
    method: 'POST',
    body: { e164s },
    responseType: 'json',
  })
}

export interface CallbackNumbersState {
  me: { phone_number: string; label: string; status: TelephonyCallbackStatus } | null
  team_default: { phone_number: string | null; verified: boolean; pending_number: string | null }
}

export function getCallbackNumbers(): Promise<CallbackNumbersState> {
  return apiFetch<CallbackNumbersState>('/api/dialer/telephony/callback-numbers')
}

export interface CallbackVerificationResult {
  // The NORMALIZED number the server stamped — not necessarily the string we sent
  // (+4407911123456 normalizes to +447911123456). Track this, not the raw input, when
  // comparing against the stored number to decide whether our candidate landed.
  phone_number: string
  validation_code: string
  // The number was already a verified caller ID.
  already_verified: boolean
  expires_at: string
}

export function startCallbackVerification(body: {
  phone_number: string
  label?: string
  target: CallbackTarget
}): Promise<CallbackVerificationResult> {
  return apiFetch<CallbackVerificationResult>('/api/dialer/telephony/callback-verification', {
    method: 'POST',
    body,
    responseType: 'json',
  })
}

export function recallCallbackVerification(
  target: CallbackTarget,
): Promise<CallbackVerificationResult> {
  return apiFetch<CallbackVerificationResult>('/api/dialer/telephony/callback-verification/recall', {
    method: 'POST',
    body: { target },
    responseType: 'json',
  })
}

export function cancelCallbackVerification(target: CallbackTarget): Promise<unknown> {
  return apiFetch('/api/dialer/telephony/callback-verification/cancel', {
    method: 'POST',
    body: { target },
    responseType: 'json',
  })
}

export function putGuardrails(body: {
  calling_hours?: TelephonyCallingHours
  recording: TelephonyRecording
}): Promise<TelephonyProjection> {
  return apiFetch<TelephonyProjection>('/api/dialer/telephony/guardrails', {
    method: 'PUT',
    body,
    responseType: 'json',
  })
}

export interface TelephonyVoice {
  voice_id: string
  name: string
  description: string | null
  gender: string | null
  accent: string | null
  preview_url: string | null
}

export function getTelephonyVoices(): Promise<{ voices: TelephonyVoice[] }> {
  return apiFetch<{ voices: TelephonyVoice[] }>('/api/dialer/telephony/voices')
}

export function putTelephonyMe(body: {
  voicemail_enabled?: boolean
  voice_id?: string
  dev_override_number?: string | null
}): Promise<TelephonyProjection> {
  return apiFetch<TelephonyProjection>('/api/dialer/telephony/me', {
    method: 'PUT',
    body,
    responseType: 'json',
  })
}
